import * as BaseRadioGroup from "@radix-ui/react-radio-group";
import styled from "@emotion/styled";
import React from "react";
import { Typography } from "../Typography";

type Orientation = "horizontal" | "vertical";

type RootProps = {
  orientation: Orientation;
};

const Root = styled(BaseRadioGroup.Root)<RootProps>`
  display: flex;
  flex-direction: ${(props) =>
    props.orientation === "horizontal" ? "row" : "column"};
  justify-content: ${(props) =>
    props.orientation === "horizontal" ? "none" : "center"};
  align-items: ${(props) =>
    props.orientation === "horizontal" ? "center" : "none"};
  gap: 22px;
`;

const Label = styled(Typography)`
  margin-bottom: 36px;
  display: block;
`;

const Item = styled(BaseRadioGroup.Item)`
  all: unset;
  background-color: white;
  width: 24px;
  border: 1px solid ${({ theme }) => theme.colors.cloudDark};
  height: 24px;
  cursor: pointer;
  border-radius: 100%;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.forest};
    outline-offset: 2px;
  }
  &[data-state="checked"] {
    background-color: ${({ theme }) => theme.colors.jungleDark};
    border: 1px solid transparent;
  }
`;

const Indicator = styled(BaseRadioGroup.Indicator)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;

  &::after {
    content: "";
    display: block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: white;
  }
`;

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export type GroupItem = {
  label: string;
  value: string;
};

type Props = {
  label?: string;
  labelHidden?: boolean;
  items: GroupItem[];
  defaultValue?: string;
  value: string;
  onValueChange: (value: string) => void;
  ariaLabel: string;
  orientation?: Orientation;
  className?: string;
  id?: string;
};

export const RadioGroup = ({
  items,
  defaultValue,
  label,
  onValueChange,
  value,
  ariaLabel,
  labelHidden = false,
  orientation = "vertical",
  className,
  id,
}: Props) => {
  return (
    <div>
      {!labelHidden && label && (
        <Label as="label" use="textInputLabel">
          {label}
        </Label>
      )}

      <Root
        defaultValue={defaultValue}
        aria-label={ariaLabel}
        orientation={orientation}
        value={value}
        onValueChange={onValueChange}
        className={className}
        id={id}
      >
        {items.map((item, index) => (
          <ItemContainer key={item.value}>
            <Item
              value={item.value}
              id={`option-${index}`}
              data-testid={item.value}
            >
              <Indicator />
            </Item>
            <Typography as="label" use="body2" htmlFor={`option-${index}`}>
              {item.label}
            </Typography>
          </ItemContainer>
        ))}
      </Root>
    </div>
  );
};
